import React, { useState, useCallback } from 'react';
import { useRecorder } from '../../hooks/useRecorder';
import useRecordingTimer from '../../hooks/useRecordingTimer';

const RecordingContext = React.createContext();

export function RecordingProvider({ children }) {
  // Recording state and logic
  const recorder = useRecorder();
  const timer = useRecordingTimer();

  // UI state: modal vs bar
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  // Derived: show bar if not modal and recording or paused
  const showRecordingBar = !showRecordingModal && (recorder.isRecording || recorder.isPaused);

  // Patient state
  const [patient, setPatient] = useState(null);
  const [fromPatientDetails, setFromPatientDetails] = useState(false);

  // Notes state
  const [userNotes, setUserNotes] = useState('');
  const resetUserNotes = useCallback(() => setUserNotes(''), []);

  console.log('[RecordingContext] Render:', {
    showRecordingModal,
    isRecording: recorder.isRecording,
    paused: recorder.isPaused,
    showRecordingBar,
    patient
  });

  // Start recording
  const startRecording = useCallback(async () => {
    console.log('[RecordingContext] startRecording called');
    await recorder.start();
    setShowRecordingModal(false);
    timer.start();
    console.log('[RecordingContext] startRecording done', {
      showRecordingModal,
      isRecording: recorder.isRecording,
      paused: recorder.isPaused
    });
  }, [recorder, timer]);

  // Pause recording
  const pauseRecording = useCallback(async () => {
    console.log('[RecordingContext] pauseRecording called');
    recorder.pause();
    timer.pause();
    setShowRecordingModal(true);
    console.log('[RecordingContext] pauseRecording done', {
      showRecordingModal,
      isRecording: recorder.isRecording,
      paused: recorder.isPaused
    });
  }, [recorder, timer]);

  // Resume recording
  const resumeRecording = useCallback(async () => {
    recorder.resume();
    timer.start();
    setShowRecordingModal(false);
  }, [recorder, timer]);

  // Expand modal from bar (patient/notes/pause)
  const expandModal = useCallback(() => {
    setShowRecordingModal(true);
    recorder.pause();
    timer.pause();
    console.log('[RecordingContext] expandModal', {
      showRecordingModal,
      isRecording: recorder.isRecording,
      paused: recorder.isPaused
    });
  }, [timer]);

  // End recording (cancel)
  const endRecording = useCallback(async () => {
    await recorder.end();
    setShowRecordingModal(false);
    timer.reset();
    resetUserNotes();
    console.log('[RecordingContext] endRecording', {
      showRecordingModal,
      isRecording: recorder.isRecording,
      paused: recorder.isPaused
    });
  }, [recorder, timer, resetUserNotes]);

  // Update patient state when selected in RecordingModal
  const updatePatient = useCallback((newPatient) => {
    setPatient(newPatient);
    console.log('[RecordingContext] Patient updated:', newPatient);
  }, []);

  // Open new visit (higher-level API)
  const openNewVisit = useCallback((newPatient = null, fromDetails = false) => {
    setPatient(newPatient);
    setFromPatientDetails(fromDetails);
    setShowRecordingModal(true);
    resetUserNotes();
    console.log('[RecordingContext] openNewVisit:', { newPatient, fromDetails });
  }, [resetUserNotes]);

  // Pause only (no modal)
  const pauseOnly = useCallback(() => {
    recorder.pause();
    timer.pause();
  }, [recorder, timer]);

  const hasActiveSession = recorder.isRecording || recorder.isPaused;
  const sessionUuid = recorder.sessionUuid; // adjust if sessionUuid is stored elsewhere

  return (
    <RecordingContext.Provider
      value={{
        // UI state
        showRecordingModal,
        setShowRecordingModal,
        showRecordingBar,
        // Recording state
        ...recorder,
        elapsedTime: timer.elapsedTime,
        startRecording,
        pauseRecording,
        pauseOnly,
        resumeRecording,
        endRecording,
        // Modal/Bar controls
        expandModal,
        // Patient
        patient,
        setPatient: updatePatient,
        fromPatientDetails,
        openNewVisit,
        // Notes
        userNotes,
        setUserNotes,
        resetUserNotes,
        hasActiveSession,
        sessionUuid
      }}
    >
      {children}
    </RecordingContext.Provider>
  );
}

export default RecordingContext;
